<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import { useTracking } from 'dashboard/composables/useTracking';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  summary: {
    type: Object,
    required: true,
  },
  align: {
    type: String,
    default: 'right',
  },
});

const loadingApprovalId = ref(null);
const decisionError = ref('');
const localSummary = ref({});
const localApprovals = ref([]);
const { t } = useI18n();
const { track } = useTracking();
const { checkPermissions } = usePolicy();
const canManageApprovals = computed(() => checkPermissions(['administrator']));

const structuredFields = [
  {
    key: 'decisionOutcome',
    keys: ['decisionOutcome', 'decision_outcome'],
    aliases: [
      'decision outcome',
      'human decision',
      'decisao humana',
      'resultado da decisao',
    ],
    icon: 'i-lucide-shield-check',
    formatter: 'decisionOutcome',
  },
  {
    key: 'customerFollowUpRequired',
    keys: ['customerFollowUpRequired', 'customer_follow_up_required'],
    aliases: [
      'customer follow up required',
      'customer follow-up required',
      'retorno ao cliente necessario',
      'cliente aguarda retorno',
    ],
    icon: 'i-lucide-message-circle-warning',
    formatter: 'boolean',
  },
  {
    key: 'handoffReason',
    keys: [
      'handoffReason',
      'handoff_reason',
      'handoffTrigger',
      'handoff_trigger',
    ],
    aliases: [
      'handoff reason',
      'handoff',
      'motivo do handoff',
      'motivo de handoff',
      'motivo da transferencia',
      'motivo de transferencia',
    ],
    icon: 'i-lucide-forward',
  },
  {
    key: 'handoffContext',
    keys: [
      'handoffContext',
      'handoff_context',
      'transferContext',
      'transfer_context',
    ],
    aliases: [
      'handoff context',
      'contexto do handoff',
      'contexto de handoff',
      'contexto da transferencia',
      'contexto de transferencia',
    ],
    icon: 'i-lucide-message-square-share',
  },
  {
    key: 'qualificationStatus',
    keys: [
      'qualificationStatus',
      'qualification_status',
      'qualification',
      'leadQualification',
      'lead_qualification',
    ],
    aliases: [
      'qualification status',
      'qualification',
      'qualificacao',
      'status da qualificacao',
      'status de qualificacao',
    ],
    icon: 'i-lucide-badge-check',
  },
  {
    key: 'confidence',
    keys: [
      'confidence',
      'confidenceScore',
      'confidence_score',
      'qualificationConfidence',
      'qualification_confidence',
    ],
    aliases: [
      'confidence',
      'confidence score',
      'confianca',
      'score de confianca',
      'nivel de confianca',
    ],
    icon: 'i-lucide-gauge',
    formatter: 'confidence',
  },
];

const readField = (object, camelKey, snakeKey) =>
  object?.[camelKey] ?? object?.[snakeKey];

const normalizeDetailLabel = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const normalizeDisplayValue = value => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(normalizeDisplayValue).join(', ');
  if (typeof value === 'object') {
    return normalizeDisplayValue(value.label ?? value.value ?? value.name);
  }

  return String(value).trim();
};

const formatConfidence = value => {
  const displayValue = normalizeDisplayValue(value);
  if (!displayValue) return '';
  if (displayValue.includes('%')) return displayValue;

  const numericValue = Number(displayValue.replace(',', '.'));
  if (!Number.isFinite(numericValue)) return displayValue;

  const percentage = numericValue <= 1 ? numericValue * 100 : numericValue;
  return `${Math.round(percentage)}%`;
};

const detailEntries = computed(() =>
  Array.isArray(localSummary.value.details) ? localSummary.value.details : []
);

const detailAliases = computed(() => {
  return structuredFields.reduce((aliases, field) => {
    field.aliases.forEach(alias => aliases.add(normalizeDetailLabel(alias)));
    return aliases;
  }, new Set());
});

const findDetailValue = field => {
  const normalizedAliases = field.aliases.map(normalizeDetailLabel);
  const detail = detailEntries.value.find(entry => {
    const label = normalizeDetailLabel(entry.key ?? entry.label ?? entry.name);
    return normalizedAliases.includes(label);
  });

  return detail?.value;
};

const readFirstAvailableField = field => {
  const explicitValue = field.keys
    .map(key => localSummary.value?.[key])
    .find(value => normalizeDisplayValue(value));

  return explicitValue ?? findDetailValue(field);
};

const structuredLabel = key => {
  const labels = {
    decisionOutcome: t(
      'CONVERSATION.AGENT_SUMMARY.STRUCTURED.DECISION_OUTCOME'
    ),
    customerFollowUpRequired: t(
      'CONVERSATION.AGENT_SUMMARY.STRUCTURED.CUSTOMER_FOLLOW_UP'
    ),
    handoffReason: t('CONVERSATION.AGENT_SUMMARY.STRUCTURED.HANDOFF_REASON'),
    handoffContext: t('CONVERSATION.AGENT_SUMMARY.STRUCTURED.HANDOFF_CONTEXT'),
    qualificationStatus: t(
      'CONVERSATION.AGENT_SUMMARY.STRUCTURED.QUALIFICATION'
    ),
    confidence: t('CONVERSATION.AGENT_SUMMARY.STRUCTURED.CONFIDENCE'),
  };

  return labels[key] || '';
};

const normalizeApproval = approval => ({
  id: approval.id,
  requestedAction: readField(approval, 'requestedAction', 'requested_action'),
  reason: approval.reason,
  preview: approval.preview,
  status: approval.status,
  statusLabel: readField(approval, 'statusLabel', 'status_label'),
  decisionOutcome: readField(approval, 'decisionOutcome', 'decision_outcome'),
  executionStatus: readField(approval, 'executionStatus', 'execution_status'),
  executionSucceeded: readField(
    approval,
    'executionSucceeded',
    'execution_succeeded'
  ),
  actionResult: readField(approval, 'actionResult', 'action_result'),
});

const formatDecisionOutcome = value => {
  const normalizedValue = normalizeDisplayValue(value).toLowerCase();

  if (normalizedValue === 'pending') {
    return t('CONVERSATION.AGENT_SUMMARY.OUTCOME.PENDING');
  }
  if (normalizedValue === 'approved_and_executed') {
    return t('CONVERSATION.AGENT_SUMMARY.OUTCOME.APPROVED_AND_EXECUTED');
  }
  if (normalizedValue === 'approved_but_failed') {
    return t('CONVERSATION.AGENT_SUMMARY.OUTCOME.APPROVED_BUT_FAILED');
  }
  if (normalizedValue === 'rejected') {
    return t('CONVERSATION.AGENT_SUMMARY.OUTCOME.REJECTED');
  }

  return normalizeDisplayValue(value);
};

const formatBoolean = value =>
  value === true || value === 'true' || value === 1 || value === '1'
    ? t('CONVERSATION.AGENT_SUMMARY.YES')
    : t('CONVERSATION.AGENT_SUMMARY.NO');

const formatStructuredValue = (field, value) => {
  if (field.formatter === 'confidence') return formatConfidence(value);
  if (field.formatter === 'decisionOutcome') {
    return formatDecisionOutcome(value);
  }
  if (field.formatter === 'boolean') return formatBoolean(value);

  return normalizeDisplayValue(value);
};

watch(
  () => props.summary,
  summary => {
    localSummary.value = { ...(summary || {}) };
    localApprovals.value = (summary?.approvals || []).map(normalizeApproval);
    decisionError.value = '';
  },
  { immediate: true, deep: true }
);

const detailSeparator = computed(() =>
  t('CONVERSATION.AGENT_SUMMARY.DETAIL_SEPARATOR')
);
const registeredDecisionText = computed(() =>
  t('CONVERSATION.AGENT_SUMMARY.DECISION_REGISTERED')
);
const title = computed(
  () =>
    localSummary.value.title || t('CONVERSATION.AGENT_SUMMARY.TITLE_FALLBACK')
);
const description = computed(() => localSummary.value.description || '');
const statusLabel = computed(
  () => readField(localSummary.value, 'statusLabel', 'status_label') || ''
);
const structuredDetails = computed(() =>
  structuredFields
    .map(field => {
      const rawValue = readFirstAvailableField(field);
      const value = formatStructuredValue(field, rawValue);

      return {
        key: field.key,
        icon: field.icon,
        label: structuredLabel(field.key),
        value,
      };
    })
    .filter(detail => detail.value)
);
const details = computed(() =>
  detailEntries.value.filter(entry => {
    const label = normalizeDetailLabel(entry.key ?? entry.label ?? entry.name);
    return !detailAliases.value.has(label);
  })
);
const pendingApprovals = computed(() =>
  localApprovals.value.filter(approval => approval.status === 'pending')
);
const hasResolvedApprovals = computed(() =>
  Boolean(
    readField(localSummary.value, 'decisionOutcome', 'decision_outcome') ||
      localApprovals.value.some(approval => approval.status !== 'pending')
  )
);

const containerAlignClass = computed(() =>
  props.align === 'right' ? 'self-end' : 'self-start'
);

const approvalCopy = approval =>
  approval.preview ||
  approval.reason ||
  t('CONVERSATION.AGENT_SUMMARY.REVIEW_HINT');

const replaceApproval = updatedApproval => {
  const normalizedApproval = normalizeApproval(updatedApproval);
  localApprovals.value = localApprovals.value.map(approval =>
    approval.id === normalizedApproval.id ? normalizedApproval : approval
  );
};

const responsePayload = responseOrError =>
  responseOrError?.data?.payload ||
  responseOrError?.response?.data?.payload ||
  null;

const applyDecisionPayload = payload => {
  if (!payload) return;

  if (payload.run_summary) {
    localSummary.value = {
      ...localSummary.value,
      ...payload.run_summary,
    };
    localApprovals.value = (payload.run_summary.approvals || []).map(
      normalizeApproval
    );
    return;
  }

  replaceApproval(payload);
};

const trackDecision = (approval, decision) => {
  track(ACCOUNT_EVENTS.CAPTAIN_AGENT_APPROVAL_DECIDED, {
    approvalId: approval.id,
    decision,
    source: 'conversation',
    summaryStatus: statusLabel.value || localSummary.value.status || '',
  });
};

const decideApproval = async (approval, decision) => {
  if (!canManageApprovals.value || loadingApprovalId.value) return;

  loadingApprovalId.value = approval.id;
  decisionError.value = '';

  try {
    const response =
      decision === 'approve'
        ? await CaptainAssistantAPI.approveAction({
            approvalId: approval.id,
            decisionPayload: { decided_from: 'conversation' },
          })
        : await CaptainAssistantAPI.rejectAction({
            approvalId: approval.id,
            decisionPayload: { decided_from: 'conversation' },
          });

    const payload = responsePayload(response);
    applyDecisionPayload(payload);
    trackDecision(approval, decision);
  } catch (error) {
    const payload = responsePayload(error);
    if (payload?.decision_outcome === 'approved_but_failed') {
      applyDecisionPayload(payload);
      trackDecision(approval, decision);
      decisionError.value =
        payload.action_result?.error_message ||
        t('CONVERSATION.AGENT_SUMMARY.EXECUTION_ERROR');
      return;
    }

    decisionError.value = t('CONVERSATION.AGENT_SUMMARY.DECISION_ERROR');
  } finally {
    loadingApprovalId.value = null;
  }
};
</script>

<template>
  <section
    class="w-full max-w-[34rem] rounded-lg border border-lais-border bg-white px-3 py-2 text-left shadow-sm"
    :class="containerAlignClass"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <div class="text-xs font-semibold text-lais-text">
          {{ title }}
        </div>
        <p
          v-if="description"
          class="mt-1 text-xs leading-5 text-lais-text-muted"
        >
          {{ description }}
        </p>
      </div>
      <span
        v-if="statusLabel"
        class="flex-shrink-0 rounded-md bg-lais-bg-soft px-2 py-1 text-[11px] font-medium text-lais-text-muted"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div v-if="structuredDetails.length" class="mt-2 grid gap-1.5">
      <div
        v-for="detail in structuredDetails"
        :key="detail.key"
        class="grid grid-cols-[auto_1fr] gap-x-2 rounded-md border border-lais-border-soft bg-lais-bg-soft px-2 py-1.5"
      >
        <span :class="detail.icon" class="mt-0.5 size-3.5 text-lais-primary" />
        <div class="min-w-0">
          <p class="text-[11px] font-semibold text-lais-text">
            {{ detail.label }}
          </p>
          <p class="break-words text-[11px] leading-5 text-lais-text-muted">
            {{ detail.value }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="details.length" class="mt-2 flex flex-wrap gap-1.5">
      <span
        v-for="detail in details"
        :key="detail.label"
        class="rounded-md bg-lais-bg-soft px-2 py-1 text-[11px] text-lais-text-muted"
      >
        {{ detail.label }}{{ detailSeparator }}{{ detail.value }}
      </span>
    </div>

    <div v-if="pendingApprovals.length" class="mt-3 flex flex-col gap-2">
      <article
        v-for="approval in pendingApprovals"
        :key="approval.id"
        class="rounded-md border border-lais-border bg-lais-bg-soft p-2"
      >
        <div class="text-xs font-medium text-lais-text">
          {{ approval.requestedAction }}
        </div>
        <p class="mt-1 text-xs leading-5 text-lais-text-muted">
          {{ approvalCopy(approval) }}
        </p>
        <div
          v-if="canManageApprovals"
          class="mt-2 flex flex-wrap justify-end gap-2"
        >
          <Button
            size="xs"
            color="slate"
            variant="faded"
            icon="i-lucide-x"
            :label="t('CONVERSATION.AGENT_SUMMARY.REJECT')"
            :is-loading="loadingApprovalId === approval.id"
            @click="decideApproval(approval, 'reject')"
          />
          <Button
            size="xs"
            icon="i-lucide-check"
            :label="t('CONVERSATION.AGENT_SUMMARY.APPROVE')"
            :is-loading="loadingApprovalId === approval.id"
            @click="decideApproval(approval, 'approve')"
          />
        </div>
      </article>
    </div>

    <p
      v-if="hasResolvedApprovals"
      class="mt-2 text-[11px] text-lais-text-muted"
    >
      {{ registeredDecisionText }}
    </p>
    <p v-if="decisionError" class="mt-2 text-xs text-lais-danger">
      {{ decisionError }}
    </p>
  </section>
</template>
